import { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Space, Spin, Empty, Typography, Tabs, Tag, Input, Select, Tooltip, message } from 'antd';
import { FileExcelOutlined, FilePdfOutlined, TableOutlined, UnorderedListOutlined, SyncOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { displayNumbers, displayNum } from '../../../../variables/numbersFunctions';
import { loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY } from '../etatsSinistresFunctions';

const { Text } = Typography;
const PRIMARY = '#242166';

const fmtMontant = (v) => displayNumbers(Math.round(Number(v) || 0)) || '0';       // écran (espace fine OK en HTML)
const fmtPdf = (v) => { const n = Math.round(Number(v) || 0); return n === 0 ? '0' : displayNum(n); }; // PDF : espaces normales
const fmtPct = (v) => (v || v === 0 ? `${(Number(v) || 0).toFixed(2)} %` : '');
const frDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '');

// Quote-part par branche (repli sur quotePart si l'ancien back n'est pas redéployé) :
// la clé GUM et la clé VT sont indépendantes, on affiche celle de la branche montrée.
const qpGumOf = (m) => (m?.quotePartGum ?? m?.quotePart);
const qpVtOf = (m) => (m?.quotePartVt ?? m?.quotePart);

/**
 * État « SAP GUM & VT » du POOL — tableau croisé exercices × compagnies membres.
 * `branche` = 'GUM' | 'VT' | null : filtre les colonnes (null = cumul des deux).
 */
const PoolGumVtEtat = ({ arreteAu, branche, inclureOgar = false }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const showGum = branche !== 'VT';
    const showVt = branche !== 'GUM';
    const both = !branche;
    // arreteAu null = SAP COURANT (persisté) ; une date = reconstruction à cette date passée.
    const arreteLabel = arreteAu ? frDate(arreteAu) : 'ce jour';
    const arreteFileLabel = arreteAu ? frDate(arreteAu).replace(/\//g, '-') : 'courant';
    const titre = `SAP ${both ? 'GUM & VT' : branche} ${arreteAu ? `vus au ${arreteLabel}` : '(courant)'}`;

    useEffect(() => {
        setLoading(true);
        const params = { inclureOgar };
        if (arreteAu) params.arreteAu = dayjs(arreteAu).format('YYYY-MM-DD');
        axiosInstance.get('/api/stats/sap/pool-gum-vt', { params })
            .then((res) => setData(res.data))
            .catch((err) => { console.error(err); setData(null); })
            .finally(() => setLoading(false));
    }, [arreteAu, inclureOgar]);

    // ── Onglet « Détails par dossier » : une ligne par sinistre GUM/VT, chargée
    //    paresseusement à la première activation (invalidée quand la date d'arrêté change).
    const [activeTab, setActiveTab] = useState('etat');
    const [detailRows, setDetailRows] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailSearch, setDetailSearch] = useState('');
    const [detailExercice, setDetailExercice] = useState(null);

    useEffect(() => { setDetailRows(null); }, [arreteAu, inclureOgar]);
    useEffect(() => {
        if (activeTab !== 'details' || detailRows !== null) return;
        setDetailLoading(true);
        const params = { inclureOgar };
        if (arreteAu) params.arreteAu = dayjs(arreteAu).format('YYYY-MM-DD');
        axiosInstance.get('/api/stats/sap/pool-gum-vt/detail', { params })
            .then((res) => setDetailRows(res.data || []))
            .catch((err) => { console.error(err); setDetailRows([]); })
            .finally(() => setDetailLoading(false));
    }, [activeTab, detailRows, arreteAu, inclureOgar]);

    const detailFiltres = useMemo(() => {
        let rows = detailRows || [];
        if (branche) rows = rows.filter((r) => r.branche === branche);
        if (detailExercice) rows = rows.filter((r) => r.annee === detailExercice);
        const q = detailSearch.trim().toLowerCase();
        if (q) {
            rows = rows.filter((r) =>
                [r.numeroSinistre, r.numeroSinistrem, r.numeroPolice, r.souscripteur, r.compagnieNom]
                    .some((v) => (v || '').toLowerCase().includes(q)));
        }
        return rows;
    }, [detailRows, branche, detailExercice, detailSearch]);

    const detailExercices = useMemo(
        () => Array.from(new Set((detailRows || []).map((r) => r.annee))).sort(),
        [detailRows]
    );
    const detailTotaux = useMemo(() => detailFiltres.reduce((acc, r) => ({
        evaluation: acc.evaluation + (Number(r.evaluation) || 0),
        regle: acc.regle + (Number(r.montantRegle) || 0),
        sap: acc.sap + (Number(r.sap) || 0),
    }), { evaluation: 0, regle: 0, sap: 0 }), [detailFiltres]);

    // Fraîcheur : le détail est servi par une vue matérialisée (mv_sap_pool_gum_vt_detail),
    // rafraîchie toutes les 30 min côté serveur + à la demande via le bouton « Actualiser ».
    const detailGenereLe = detailRows?.length ? detailRows[0].genereLe : null;
    const [refreshLance, setRefreshLance] = useState(false);
    const handleDetailRefresh = async () => {
        try {
            const res = await axiosInstance.post('/api/stats/sap/pool-gum-vt/detail/refresh');
            setRefreshLance(true);
            message.info(res?.data?.message || 'Rafraîchissement lancé en arrière-plan.');
        } catch (err) {
            if (err?.response?.status === 409) message.warning(err.response.data?.message || 'Un rafraîchissement est déjà en cours.');
            else { console.error(err); message.error('Impossible de lancer le rafraîchissement.'); }
        }
    };

    // Exercice affiché : année seule si le régime couvre l'année entière, sinon la période.
    const periodeLabel = (r) => {
        const debut = dayjs(r.regimeDebut), fin = dayjs(r.regimeFin);
        const pleinExercice = debut.format('MM-DD') === '01-01' && fin.format('MM-DD') === '12-31';
        return pleinExercice ? String(r.annee) : `${r.annee} (${debut.format('DD/MM')} → ${fin.format('DD/MM')})`;
    };

    const compagnies = data?.compagnies || [];

    const dataSource = useMemo(() => {
        if (!data) return [];
        const rows = (data.lignes || []).map((l, i) => {
            const m = {};
            (l.montants || []).forEach((x) => { m[x.compagnieCode] = x; });
            return { key: `l${i}`, __type: 'data', label: l.label, sapGum: l.sapGum, sapVt: l.sapVt, total: l.total, m };
        });
        const tp = {};
        (data.totauxParCompagnie || []).forEach((x) => { tp[x.compagnieCode] = x; });
        rows.push({
            key: 'totaux', __type: 'totaux', label: 'TOTAUX',
            sapGum: data.totalSapGum, sapVt: data.totalSapVt, total: data.totalGeneral, tp,
        });
        rows.push({ key: 'totComp', __type: 'totComp', label: 'TOTAUX PAR COMPAGNIE', tp });
        return rows;
    }, [data]);

    // Nombre de colonnes de tête visibles (Exercices + colonnes SAP) → colSpan de la ligne "TOTAUX PAR COMPAGNIE"
    const baseCount = 1 + (showGum ? 1 : 0) + (showVt ? 1 : 0) + (both ? 1 : 0);

    const columns = useMemo(() => {
        const bold = (row) => (row.__type !== 'data' ? { fontWeight: 700 } : {});
        const base = [
            {
                title: 'Exercices', dataIndex: 'label', key: 'label', fixed: 'left', width: 130,
                onCell: (row) => (row.__type === 'totComp' ? { colSpan: baseCount } : {}),
                render: (v, row) => <span style={{ ...bold(row), color: row.__type !== 'data' ? PRIMARY : undefined }}>{v}</span>,
            },
        ];
        if (showGum) base.push({
            title: 'GUICHET UNIQUE DE MOTO', dataIndex: 'sapGum', key: 'sapGum', align: 'right', width: 130,
            onCell: (row) => (row.__type === 'totComp' ? { colSpan: 0 } : {}),
            render: (v, row) => <span style={bold(row)}>{fmtMontant(v)}</span>,
        });
        if (showVt) base.push({
            title: 'VEHICULE EN TRANSIT', dataIndex: 'sapVt', key: 'sapVt', align: 'right', width: 130,
            onCell: (row) => (row.__type === 'totComp' ? { colSpan: 0 } : {}),
            render: (v, row) => <span style={bold(row)}>{fmtMontant(v)}</span>,
        });
        if (both) base.push({
            title: 'TOTAL', dataIndex: 'total', key: 'total', align: 'right', width: 130,
            onCell: (row) => (row.__type === 'totComp' ? { colSpan: 0 } : {}),
            render: (v, row) => <span style={{ ...bold(row), color: PRIMARY }}>{fmtMontant(v)}</span>,
        });

        const montantGum = (row, code) => {
            if (row.__type === 'totComp') return fmtMontant(both ? row.tp[code]?.totalCompagnie : row.tp[code]?.totalMontantGum);
            if (row.__type === 'totaux') return fmtMontant(row.tp[code]?.totalMontantGum);
            return fmtMontant(row.m[code]?.montantGum);
        };
        const montantVt = (row, code) => {
            if (row.__type === 'totComp') return both ? '' : fmtMontant(row.tp[code]?.totalMontantVt);
            if (row.__type === 'totaux') return fmtMontant(row.tp[code]?.totalMontantVt);
            return fmtMontant(row.m[code]?.montantVt);
        };

        // Quote-part de la branche affichée : en vue GUM le taux GUM, en vue VT le taux VT.
        const qpGum = qpGumOf;
        const qpVt = qpVtOf;

        const compCols = compagnies.map((c) => {
            const montantChildren = [];
            if (showGum) montantChildren.push({
                title: 'GUM', key: `${c.code}_g`, align: 'right', width: 120,
                // fusionne GUM+VT pour la ligne "TOTAUX PAR COMPAGNIE" uniquement quand les 2 branches sont visibles
                onCell: (row) => (row.__type === 'totComp' && both ? { colSpan: 2 } : {}),
                render: (_, row) => <span style={bold(row)}>{montantGum(row, c.code)}</span>,
            });
            if (showVt) montantChildren.push({
                title: 'VT', key: `${c.code}_v`, align: 'right', width: 120,
                onCell: (row) => (row.__type === 'totComp' && both ? { colSpan: 0 } : {}),
                render: (_, row) => <span style={bold(row)}>{montantVt(row, c.code)}</span>,
            });

            // Quote-part : une seule colonne dans une vue mono-branche ; DEUX colonnes (GUM %, VT %)
            // dans la vue combinée, car les deux clés diffèrent (ex. 0 % GUM vs 20 % VT) et une
            // quote-part unique donnerait « 20 % en face d'un montant GUM nul ».
            let quotePartChild;
            if (both) {
                quotePartChild = {
                    title: 'Quote part',
                    children: [
                        {
                            title: 'GUM', key: `${c.code}_qpg`, align: 'center', width: 70,
                            render: (_, row) => (row.__type === 'data' ? fmtPct(qpGum(row.m[c.code])) : ''),
                        },
                        {
                            title: 'VT', key: `${c.code}_qpv`, align: 'center', width: 70,
                            render: (_, row) => (row.__type === 'data' ? fmtPct(qpVt(row.m[c.code])) : ''),
                        },
                    ],
                };
            } else {
                const qp = showGum ? qpGum : qpVt;
                quotePartChild = {
                    title: 'Quote part', key: `${c.code}_qp`, align: 'center', width: 90,
                    render: (_, row) => (row.__type === 'data' ? fmtPct(qp(row.m[c.code])) : ''),
                };
            }

            return {
                title: c.nom,
                key: `c_${c.code}`,
                children: [
                    quotePartChild,
                    { title: 'Montant', children: montantChildren },
                ],
            };
        });

        return [...base, ...compCols];
    }, [compagnies, showGum, showVt, both, baseCount]);

    // Colonnes à plat (Excel) selon la branche
    const flatHeaders = () => {
        const h = ['Exercices'];
        if (showGum) h.push('GUICHET UNIQUE DE MOTO');
        if (showVt) h.push('VEHICULE EN TRANSIT');
        if (both) h.push('TOTAL');
        compagnies.forEach((c) => {
            // Deux quote-parts distinctes en vue combinée (clés GUM et VT indépendantes).
            if (both) {
                h.push(`${c.nom} - Quote part GUM`);
                h.push(`${c.nom} - Quote part VT`);
            } else {
                h.push(`${c.nom} - Quote part`);
            }
            if (showGum) h.push(`${c.nom} - GUM`);
            if (showVt) h.push(`${c.nom} - VT`);
        });
        return h;
    };

    const handleExcel = () => {
        if (!data) return;
        const aoa = [[titre], ["Méthode d'évaluation : Dossier par dossier"], [], flatHeaders()];
        const num = (v) => Math.round(Number(v) || 0);

        (data.lignes || []).forEach((l) => {
            const m = {};
            (l.montants || []).forEach((x) => { m[x.compagnieCode] = x; });
            const row = [l.label];
            if (showGum) row.push(num(l.sapGum));
            if (showVt) row.push(num(l.sapVt));
            if (both) row.push(num(l.total));
            compagnies.forEach((c) => {
                const pct = (v) => (v || v === 0 ? `${(Number(v) || 0).toFixed(2)} %` : '');
                if (both) {
                    row.push(pct(qpGumOf(m[c.code])));
                    row.push(pct(qpVtOf(m[c.code])));
                } else {
                    row.push(pct(showGum ? qpGumOf(m[c.code]) : qpVtOf(m[c.code])));
                }
                if (showGum) row.push(num(m[c.code]?.montantGum));
                if (showVt) row.push(num(m[c.code]?.montantVt));
            });
            aoa.push(row);
        });

        const tp = {};
        (data.totauxParCompagnie || []).forEach((x) => { tp[x.compagnieCode] = x; });
        const totRow = ['TOTAUX'];
        if (showGum) totRow.push(num(data.totalSapGum));
        if (showVt) totRow.push(num(data.totalSapVt));
        if (both) totRow.push(num(data.totalGeneral));
        compagnies.forEach((c) => {
            totRow.push('');                 // quote-part (GUM en vue combinée)
            if (both) totRow.push('');       // quote-part VT (2e colonne)
            if (showGum) totRow.push(num(tp[c.code]?.totalMontantGum));
            if (showVt) totRow.push(num(tp[c.code]?.totalMontantVt));
        });
        aoa.push(totRow);

        const totCompRow = ['TOTAUX PAR COMPAGNIE'];
        for (let i = 1; i < baseCount; i++) totCompRow.push('');
        compagnies.forEach((c) => {
            totCompRow.push(''); // Quote part (GUM en vue combinée)
            if (both) {
                totCompRow.push('');                              // Quote part VT (2e colonne)
                totCompRow.push(num(tp[c.code]?.totalCompagnie)); // colonne GUM = combiné GUM+VT
                totCompRow.push('');                              // colonne VT vide
            } else if (showGum) {
                totCompRow.push(num(tp[c.code]?.totalMontantGum));
            } else {
                totCompRow.push(num(tp[c.code]?.totalMontantVt));
            }
        });
        aoa.push(totCompRow);

        aoa.push([]);
        aoa.push(["NB: Il revient à chaque société d'appliquer le taux minimal de chargement de 05% pour déterminer la Provision des SAP"]);
        aoa.push([`Fait le ${dayjs().format('DD/MM/YYYY')}`]);
        aoa.push(['Le Directeur du POOL TPVM-VT GIE']);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `SAP ${both ? 'GUM-VT' : branche}`);
        const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([blob], { type: 'application/octet-stream' }), `SAP_${both ? 'GUM_VT' : branche}_au_${arreteFileLabel}.xlsx`);
    };

    const handlePdf = async () => {
        if (!data) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        const pw = doc.internal.pageSize.getWidth();
        const formattedNow = dayjs().format('DD/MM/YYYY HH:mm');
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        // En-têtes sur 2 lignes (groupe compagnie + sous-colonnes) selon la branche
        const head1 = [{ content: 'Exercices', rowSpan: 2 }];
        if (showGum) head1.push({ content: 'GUICHET UNIQUE DE MOTO', rowSpan: 2 });
        if (showVt) head1.push({ content: 'VEHICULE EN TRANSIT', rowSpan: 2 });
        if (both) head1.push({ content: 'TOTAL', rowSpan: 2 });
        const head2 = [];
        const montantCols = (showGum ? 1 : 0) + (showVt ? 1 : 0);
        const qpCols = both ? 2 : 1; // 2 quote-parts (GUM/VT) en vue combinée
        compagnies.forEach((c) => {
            head1.push({ content: c.nom, colSpan: qpCols + montantCols, styles: { halign: 'center' } });
            if (both) {
                head2.push('QP GUM');
                head2.push('QP VT');
            } else {
                head2.push('Quote part');
            }
            if (showGum) head2.push('GUM');
            if (showVt) head2.push('VT');
        });

        const body = (data.lignes || []).map((l) => {
            const m = {};
            (l.montants || []).forEach((x) => { m[x.compagnieCode] = x; });
            const row = [l.label];
            if (showGum) row.push(fmtPdf(l.sapGum));
            if (showVt) row.push(fmtPdf(l.sapVt));
            if (both) row.push(fmtPdf(l.total));
            compagnies.forEach((c) => {
                if (both) {
                    row.push(fmtPct(qpGumOf(m[c.code])));
                    row.push(fmtPct(qpVtOf(m[c.code])));
                } else {
                    row.push(fmtPct(showGum ? qpGumOf(m[c.code]) : qpVtOf(m[c.code])));
                }
                if (showGum) row.push(fmtPdf(m[c.code]?.montantGum));
                if (showVt) row.push(fmtPdf(m[c.code]?.montantVt));
            });
            return row;
        });

        const tp = {};
        (data.totauxParCompagnie || []).forEach((x) => { tp[x.compagnieCode] = x; });
        const totRow = [{ content: 'TOTAUX', styles: { fontStyle: 'bold' } }];
        if (showGum) totRow.push(fmtPdf(data.totalSapGum));
        if (showVt) totRow.push(fmtPdf(data.totalSapVt));
        if (both) totRow.push(fmtPdf(data.totalGeneral));
        compagnies.forEach((c) => {
            totRow.push('');                 // QP (GUM en vue combinée)
            if (both) totRow.push('');       // QP VT (2e colonne)
            if (showGum) totRow.push(fmtPdf(tp[c.code]?.totalMontantGum));
            if (showVt) totRow.push(fmtPdf(tp[c.code]?.totalMontantVt));
        });
        body.push(totRow);

        const totCompRow = [{ content: 'TOTAUX PAR COMPAGNIE', colSpan: baseCount, styles: { halign: 'right', fontStyle: 'bold' } }];
        compagnies.forEach((c) => {
            const val = both ? tp[c.code]?.totalCompagnie : (showGum ? tp[c.code]?.totalMontantGum : tp[c.code]?.totalMontantVt);
            // colSpan couvre les colonnes quote-part (qpCols) + montant (montantCols) de la compagnie.
            totCompRow.push({ content: fmtPdf(val), colSpan: qpCols + montantCols, styles: { halign: 'right', fontStyle: 'bold' } });
        });
        body.push(totCompRow);

        autoTable(doc, {
            head: [head1, head2],
            body,
            startY: 34,
            theme: 'grid',
            styles: { fontSize: 6.5, cellPadding: 1, lineWidth: 0.1, lineColor: [210, 210, 220], overflow: 'linebreak' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, halign: 'center' },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: { 0: { halign: 'left', cellWidth: 22 } },
            margin: { top: 34, bottom: 22, left: 10, right: 10 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: titre,
                subtitle: "Méthode d'évaluation : Dossier par dossier",
                enteteData, logos, formattedNow,
            }),
        });

        let y = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(8); doc.setFont(undefined, 'italic'); doc.setTextColor(60, 60, 60);
        doc.text("NB: Il revient à chaque société d'appliquer le taux minimal de chargement de 05% pour déterminer la Provision des SAP", 12, y);
        y += 12;
        doc.setFont(undefined, 'normal');
        doc.text(`Fait le ${dayjs().format('DD/MM/YYYY')}`, pw - 90, y);
        y += 7;
        doc.setFont(undefined, 'bold');
        doc.text('Le Directeur du POOL TPVM-VT GIE', pw - 90, y);

        finalizeEtatPdf(doc);
        doc.save(`SAP_${both ? 'GUM_VT' : branche}_au_${arreteFileLabel}.pdf`);
    };

    // ── Exports du DÉTAIL par dossier (mêmes gabarits que l'état croisé) ─────────
    const titreDetail = `Détail des SAP ${both ? 'GUM & VT' : branche} par dossier ${arreteAu ? `au ${arreteLabel}` : "(courant)"}`;
    const DETAIL_HEADERS = ['Exercice', 'Branche', 'N° Sinistre', 'N° Sinistre Cie', 'Survenance',
        'N° Police', 'Assuré', 'Compagnie', 'Évaluation', 'Réglé', 'SAP'];
    const detailRowCells = (r, fm) => [
        periodeLabel(r), r.branche, r.numeroSinistre || '', r.numeroSinistrem || '',
        frDate(r.dateSinistre), r.numeroPolice || '', r.souscripteur || '', r.compagnieNom || '',
        fm(r.evaluation), fm(r.montantRegle), fm(r.sap),
    ];

    const handleDetailExcel = () => {
        if (!detailFiltres.length) return;
        const num = (v) => Math.round(Number(v) || 0);
        const aoa = [[titreDetail], ["Méthode d'évaluation : Dossier par dossier"], [], DETAIL_HEADERS];
        detailFiltres.forEach((r) => aoa.push(detailRowCells(r, num)));
        aoa.push(['TOTAUX', '', '', '', '', '', '', `${detailFiltres.length} dossier(s)`,
            num(detailTotaux.evaluation), num(detailTotaux.regle), num(detailTotaux.sap)]);
        aoa.push([]);
        aoa.push([`Fait le ${dayjs().format('DD/MM/YYYY')}`]);
        aoa.push(['Le Directeur du POOL TPVM-VT GIE']);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Détail SAP');
        const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([blob], { type: 'application/octet-stream' }),
            `Detail_SAP_${both ? 'GUM_VT' : branche}_au_${arreteFileLabel}.xlsx`);
    };

    const handleDetailPdf = async () => {
        if (!detailFiltres.length) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const formattedNow = dayjs().format('DD/MM/YYYY HH:mm');
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const body = detailFiltres.map((r) => detailRowCells(r, fmtPdf));
        body.push([
            { content: 'TOTAUX', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `${detailFiltres.length} dossier(s)`, styles: { fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.evaluation), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.regle), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.sap), styles: { halign: 'right', fontStyle: 'bold' } },
        ]);

        autoTable(doc, {
            head: [DETAIL_HEADERS],
            body,
            startY: 34,
            theme: 'grid',
            styles: { fontSize: 6.5, cellPadding: 1, lineWidth: 0.1, lineColor: [210, 210, 220], overflow: 'linebreak' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, halign: 'center' },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: { 8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' } },
            margin: { top: 34, bottom: 22, left: 8, right: 8 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: titreDetail,
                subtitle: "Méthode d'évaluation : Dossier par dossier",
                enteteData, logos, formattedNow,
            }),
        });

        finalizeEtatPdf(doc);
        doc.save(`Detail_SAP_${both ? 'GUM_VT' : branche}_au_${arreteFileLabel}.pdf`);
    };

    const detailColumns = [
        { title: 'Exercice', key: 'annee', width: 110, render: (_, r) => periodeLabel(r) },
        {
            title: 'Branche', dataIndex: 'branche', key: 'branche', width: 70, align: 'center',
            render: (v) => <Tag color={v === 'GUM' ? 'purple' : 'blue'} style={{ margin: 0 }}>{v}</Tag>,
        },
        {
            title: 'N° Sinistre', key: 'numeroSinistre',
            render: (_, r) => (
                <div>
                    <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.numeroSinistre || '—'}</div>
                    {r.numeroSinistrem && <div style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace' }}>{r.numeroSinistrem}</div>}
                </div>
            ),
        },
        { title: 'Survenance', dataIndex: 'dateSinistre', key: 'dateSinistre', width: 95, render: (v) => frDate(v) },
        { title: 'N° Police', dataIndex: 'numeroPolice', key: 'numeroPolice', render: (v) => <span style={{ fontFamily: 'monospace', color: '#666' }}>{v || '—'}</span> },
        { title: 'Assuré', dataIndex: 'souscripteur', key: 'souscripteur' },
        { title: 'Compagnie', dataIndex: 'compagnieNom', key: 'compagnieNom' },
        { title: 'Évaluation', dataIndex: 'evaluation', key: 'evaluation', align: 'right', render: (v) => fmtMontant(v) },
        { title: 'Réglé', dataIndex: 'montantRegle', key: 'montantRegle', align: 'right', render: (v) => fmtMontant(v) },
        { title: 'SAP', dataIndex: 'sap', key: 'sap', align: 'right', render: (v) => <b style={{ color: PRIMARY }}>{fmtMontant(v)}</b> },
    ];

    const etatContent = (
        <Card
            title={
                <div>
                    <div style={{ fontWeight: 700, color: PRIMARY, fontSize: 16 }}>{titre}</div>
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>Méthode d'évaluation : Dossier par dossier</Text>
                </div>
            }
            extra={
                <Space>
                    <Button icon={<FileExcelOutlined />} onClick={handleExcel} disabled={!data?.lignes?.length} style={{ borderColor: '#059669', color: '#059669' }}>Excel</Button>
                    <Button icon={<FilePdfOutlined />} onClick={handlePdf} disabled={!data?.lignes?.length} style={{ borderColor: '#dc2626', color: '#dc2626' }}>PDF</Button>
                </Space>
            }
        >
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="Aucune donnée SAP GUM/VT" /> }}
                    onRow={(row) => (row.__type !== 'data' ? { style: { background: '#f2f2f8' } } : {})}
                />
            </Spin>
            <div style={{ marginTop: 16, fontStyle: 'italic', color: '#595959', fontSize: 12 }}>
                NB : Il revient à chaque société d'appliquer le taux minimal de chargement de 05 % pour déterminer la Provision des SAP.
            </div>
            <div style={{ marginTop: 24, textAlign: 'right' }}>
                <div>Fait le {dayjs().format('DD/MM/YYYY')}</div>
                <div style={{ fontWeight: 700, marginTop: 8 }}>Le Directeur du POOL TPVM-VT GIE</div>
            </div>
        </Card>
    );

    const detailContent = (
        <Card
            title={
                <div>
                    <div style={{ fontWeight: 700, color: PRIMARY, fontSize: 16 }}>{titreDetail}</div>
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                        Un dossier par ligne — les totaux SAP recoupent les cellules de l'état croisé.
                        {detailGenereLe && <> Données générées le {dayjs(detailGenereLe).format('DD/MM/YYYY HH:mm')}.</>}
                    </Text>
                </div>
            }
            extra={
                <Space>
                    <Tooltip title="Recalculer les données en arrière-plan (vue matérialisée), puis recharger l'affichage">
                        <Button icon={<SyncOutlined />} onClick={handleDetailRefresh}>Actualiser</Button>
                    </Tooltip>
                    {refreshLance && (
                        <Tooltip title="Recharger l'affichage avec les données recalculées">
                            <Button icon={<ReloadOutlined />} onClick={() => { setDetailRows(null); setRefreshLance(false); }}>Recharger</Button>
                        </Tooltip>
                    )}
                    <Button icon={<FileExcelOutlined />} onClick={handleDetailExcel} disabled={!detailFiltres.length} style={{ borderColor: '#059669', color: '#059669' }}>Excel</Button>
                    <Button icon={<FilePdfOutlined />} onClick={handleDetailPdf} disabled={!detailFiltres.length} style={{ borderColor: '#dc2626', color: '#dc2626' }}>PDF</Button>
                </Space>
            }
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Select
                    allowClear
                    placeholder="Exercice…"
                    style={{ width: 140 }}
                    value={detailExercice || undefined}
                    onChange={(v) => setDetailExercice(v || null)}
                    options={detailExercices.map((a) => ({ value: a, label: String(a) }))}
                />
                <Input.Search
                    allowClear
                    placeholder="N° sinistre, police, assuré, compagnie…"
                    style={{ width: 320 }}
                    onSearch={setDetailSearch}
                    onChange={(e) => { if (!e.target.value) setDetailSearch(''); }}
                />
                <Text type="secondary">
                    {detailFiltres.length} dossier(s) — SAP total : <b style={{ color: PRIMARY }}>{fmtMontant(detailTotaux.sap)}</b>
                </Text>
            </Space>
            <Spin spinning={detailLoading}>
                <Table
                    rowKey={(r) => r.sinistreCodeId || `${r.numeroSinistre}-${r.branche}`}
                    columns={detailColumns}
                    dataSource={detailFiltres}
                    bordered
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} dossier(s)` }}
                    locale={{ emptyText: <Empty description="Aucun dossier SAP GUM/VT" /> }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ background: '#f2f2f8', fontWeight: 700 }}>
                                <Table.Summary.Cell index={0} colSpan={7}>TOTAUX ({detailFiltres.length} dossier(s))</Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align="right">{fmtMontant(detailTotaux.evaluation)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align="right">{fmtMontant(detailTotaux.regle)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={9} align="right"><span style={{ color: PRIMARY }}>{fmtMontant(detailTotaux.sap)}</span></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </Spin>
        </Card>
    );

    return (
        <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
                { key: 'etat', label: <span><TableOutlined /> État</span>, children: etatContent },
                { key: 'details', label: <span><UnorderedListOutlined /> Détails par dossier</span>, children: detailContent },
            ]}
        />
    );
};

export default PoolGumVtEtat;
